import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import MasterLayout from "../../masterLayout/MasterLayout";
import Breadcrumb from "../Breadcrumb";
import Loader from "../child/Loder";
import DengerAlert from "../child/Alert/DengerAlert";
import { fetchPromoterBottleSaleDetail } from "../../services/promoterRequestService";
import { formatApiDate } from "../../utils/dateHelper";

const AdditionalBottleServiceView = () => {
  const { id, transactionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const listItem = location.state?.item;

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchDetail = async () => {
      if (!transactionId) {
        setLoading(false);
        setErrorMessage("Transaction id is missing.");
        return;
      }

      try {
        setLoading(true);
        setErrorMessage("");
        const response = await fetchPromoterBottleSaleDetail(transactionId);
        if (response?.data?.status) {
          const detail = response?.data?.data || null;
          setItem(
            detail
              ? {
                  ...detail,
                  transactionStatus:
                    detail.transactionStatus ||
                    listItem?.transactionStatus ||
                    listItem?.payoutStatus ||
                    "",
                  payoutStatus:
                    detail.payoutStatus || listItem?.payoutStatus || "",
                }
              : null
          );
        } else {
          setItem(null);
          setErrorMessage(
            response?.data?.message ||
              "Unable to fetch bottle sale detail."
          );
        }
      } catch (error) {
        console.error("Promoter bottle sale detail error:", error);
        setItem(null);
        setErrorMessage(
          error?.response?.data?.message ||
            "Something went wrong while fetching bottle sale detail."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [transactionId]);

  const formatSaleDate = (dateStr, saleTime) => {
    if (!dateStr) return "N/A";
    const datePart = formatApiDate(dateStr);
    if (saleTime) return `${datePart} ${saleTime}`;
    return datePart;
  };

  const formatBottleType = (category) => {
    const value = String(category || "").toLowerCase();
    if (value === "alcoholic") return "Alcoholic";
    if (value === "nonalcoholic" || value === "non-alcoholic") {
      return "Non-Alcoholic";
    }
    return category || "N/A";
  };

  const formatMoney = (value) =>
    `$${Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;

  if (loading) {
    return (
      <MasterLayout>
        <Breadcrumb
          title2="Promoter Management"
          link2="/promoter"
          title3="View Promoter"
          link3={`/promoter/view-promoter/${id}`}
          title="Additional Bottle Services"
        />
        <Loader />
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb
        title2="Promoter Management"
        link2="/promoter"
        title3="View Promoter"
        link3={`/promoter/view-promoter/${id}`}
        title="Additional Bottle Services"
      />

      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      {!item ? (
        <div className="card p-24">
          <p className="mb-3 text-muted">Transaction details not found.</p>
          {/* s */}
        </div>
      ) : (
        <div className="card h-100">
          <div className="card-body p-24">
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">Venue Name</label>
                <div>{item.hostBusinessName || "N/A"}</div>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">Event Name</label>
                <div>{item.clubName || "N/A"}</div>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">Host Name</label>
                <div>{item.hostName || "N/A"}</div>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">Promoter Name</label>
                <div>{item.promoterName || "N/A"}</div>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">Subtotal</label>
                <div>{formatMoney(item.subtotal)}</div>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">Promoter commission</label>
                <div>{formatMoney(item.commissionAmount)}</div>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">Status</label>
                <div>
                  {(() => {
                    const statusValue =
                      item.transactionStatus || item.payoutStatus || "";
                    if (!statusValue) return "N/A";
                    const normalized = String(statusValue).toLowerCase();
                    if (normalized === "paid") return "Paid";
                    if (normalized === "pending") return "Pending";
                    return (
                      statusValue.charAt(0).toUpperCase() +
                      statusValue.slice(1).toLowerCase()
                    );
                  })()}
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">Commission Type</label>
                <div>{item.commissionType || "N/A"}</div>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">Sale Date</label>
                <div>
                  {formatSaleDate(item.saleDate, item.saleTime)}
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">Created At</label>
                <div>
                  {item.createdAt
                    ? moment(item.createdAt).format("DD-MM-YYYY HH:mm")
                    : "N/A"}
                </div>
              </div>
              <div className="col-md-8 mb-3">
                <label className="form-label fw-bold">Address</label>
                <div>{item.clubAddress || "N/A"}</div>
              </div>
              {item.notes && (
                <div className="col-md-12 mb-3">
                  <label className="form-label fw-bold">Notes</label>
                  <div>{item.notes}</div>
                </div>
              )}
              {item.rejectionReason && (
                <div className="col-md-12 mb-3">
                  <label className="form-label fw-bold">
                    Rejection Reason
                  </label>
                  <div>{item.rejectionReason}</div>
                </div>
              )}
            </div>

            <div className="mt-4">
              <h6 className="fw-bold mb-3">Bottle Details</h6>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Bottle Name</th>
                      <th>Bottle Type</th>
                      <th>Quantity</th>
                      <th>Bottle Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(item.bottle) && item.bottle.length > 0 ? (
                      item.bottle.map((bottle, index) => (
                        <tr key={bottle.bottleServiceId || index}>
                          <td>{index + 1}</td>
                          <td>{bottle.bottleName || "N/A"}</td>
                          <td>
                            {formatBottleType(bottle.bottleCategory)}
                          </td>
                          <td>{bottle.quantity ?? 0}</td>
                          <td>{formatMoney(bottle.bottlePrice)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-4">
                          No bottle items found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* <div className="mt-4">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() =>
                  navigate(`/promoter/additional-bottle-service/${id}`)
                }
              >
                Back to List
              </button>
            </div> */}
          </div>
        </div>
      )}
    </MasterLayout>
  );
};

export default AdditionalBottleServiceView;
